import { MouseEvent } from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Checkbox from 'packages/ui/Checkbox'
import ChipStatus from 'packages/ui/ChipStatus'
import { IColumn, IItem } from 'packages/ui/Table'

import { DEFAULT_SITE_STATE_COLORS, SITE_STATE_CONFIG, getSiteStateLabel, isVisibleInCenter } from '../constants'
import { useSelection } from '../store'
import { SiteUnscope } from '../types'

const toSite = (item: IItem) => item as unknown as SiteUnscope

export const getItemIds = (items: IItem[]) => items.map(item => toSite(item).id)

type HeaderCheckboxProps = {
  items: IItem[]
}

const HeaderCheckbox = ({ items }: HeaderCheckboxProps) => {
  const { hasSelection, areAllSelected, clear, selectIds } = useSelection()
  const loadedIds = getItemIds(items)
  const isChecked = areAllSelected(loadedIds)

  const handleChange = (event: MouseEvent) => {
    event.stopPropagation()
    if (hasSelection) {
      clear()
      return
    }
    selectIds(loadedIds)
  }

  return (
    <Checkbox
      checked={isChecked}
      indeterminate={hasSelection && !isChecked}
      onChange={handleChange}
    />
  )
}

type BodyCheckboxProps = {
  id: string
  items: IItem[]
}

const BodyCheckbox = ({ id, items }: BodyCheckboxProps) => {
  const { isSelected, toggle } = useSelection()

  const handleChange = (event: MouseEvent) => {
    event.stopPropagation()
    if (event.shiftKey) {
      window.getSelection()?.removeAllRanges()
    }
    toggle(id, getItemIds(items), event.shiftKey)
  }

  return <Checkbox checked={isSelected(id)} onChange={handleChange} />
}

const EMPTY_VALUE = '-'

const TextCell = ({ value }: { value: string }) => (
  <Typography variant="caption">{value}</Typography>
)

export const StateCell = ({ state }: { state: string | null }) => {
  const { t } = useTranslation()
  if (!state) return <TextCell value={EMPTY_VALUE} />

  const { color, bgcolor } = SITE_STATE_CONFIG[state] ?? DEFAULT_SITE_STATE_COLORS

  return (
    <ChipStatus
      status={getSiteStateLabel(t, state)}
      color={color}
      bgcolor={bgcolor}
      height="24px"
      borderRadius="50px"
    />
  )
}

export const BooleanCell = ({ value }: { value: boolean | null }) => {
  const { t } = useTranslation()
  if (value === null) return <TextCell value={EMPTY_VALUE} />

  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <i
        aria-hidden="true"
        className={`fa-solid fa-${value ? 'circle-check' : 'circle-xmark'}`}
        style={{ color: value ? '#92BC62' : '#DADDE3', fontSize: 12 }}
      />
      <TextCell value={t(`admin_sites.filters.${value ? 'yes' : 'no'}`)} />
    </Stack>
  )
}

const VisibilityCell = ({ visibility }: { visibility: SiteUnscope['visibility_in_center'] }) => {
  const { t } = useTranslation()
  if (!visibility) return <TextCell value={EMPTY_VALUE} />
  return <TextCell value={t(`admin_sites.visibility.${visibility}`, { defaultValue: visibility })} />
}

const COLUMNS_CONFIG: IColumn[] = [
  {
    name: 'check',
    header: {
      label: ctx => <HeaderCheckbox items={ctx.items} />
    },
    value: (item, ctx) => <BodyCheckbox id={toSite(item).id} items={ctx?.items ?? []} />,
    style: { width: 24, paddingLeft: 8 }
  },
  {
    name: 'name',
    header: {
      label: ctx => ctx.t('admin_sites.table.name')
    },
    value: item => <TextCell value={toSite(item).name || EMPTY_VALUE} />,
    sortable: true,
    style: { minWidth: 220 }
  },
  {
    name: 'account_parent_name',
    header: {
      label: ctx => ctx.t('admin_sites.table.account_parent_name')
    },
    value: item => <TextCell value={toSite(item).account_parent_name || EMPTY_VALUE} />,
    style: { minWidth: 220 }
  },
  {
    name: 'state',
    header: {
      label: ctx => ctx.t('admin_sites.table.state')
    },
    value: item => <StateCell state={toSite(item).state} />,
    sortable: true
  },
  {
    name: 'has_active_project',
    header: {
      label: ctx => ctx.t('admin_sites.table.has_active_project')
    },
    value: item => <BooleanCell value={toSite(item).has_active_project} />,
    sortable: true
  },
  {
    name: 'has_process',
    header: {
      label: ctx => ctx.t('admin_sites.table.has_process')
    },
    value: item => <BooleanCell value={toSite(item).has_process} />,
    sortable: true
  },
  {
    name: 'visibility_in_center',
    header: {
      label: ctx => ctx.t('admin_sites.table.visibility_in_center')
    },
    value: item => <VisibilityCell visibility={toSite(item).visibility_in_center} />
  },
  {
    name: 'state_in_center',
    header: {
      label: ctx => ctx.t('admin_sites.table.state_in_center')
    },
    value: item => <BooleanCell value={isVisibleInCenter(toSite(item))} />
  }
]

export default COLUMNS_CONFIG
